import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, CircleAlert } from 'lucide-react';
import { SCOPE_SKILL_KEY } from '../../constants';
import type { ExamReadiness, IeltsScopeSkill } from '../../types';

interface Props {
  readiness: ExamReadiness | undefined;
  loading?: boolean;
  /** Resolves a section id to its title for the translated messages. */
  sectionTitle?: (id: string) => string | undefined;
}

const KNOWN_CODES = new Set([
  'NO_SECTIONS', 'EMPTY_SECTION', 'UNTIMED_SECTION', 'NO_AUDIO', 'UNUSUAL_LENGTH', 'NO_WRITING_TASK',
  'DUPLICATE_TASK', 'TASK_UNSET', 'NO_MODULE_1', 'MODULE_2_PAIR', 'THRESHOLD_TOO_HIGH', 'NO_SAT_SECTIONS',
  'PINNED_BLOCK_NOT_READY', 'NO_LIBRARY_MATCH', 'FEW_VERSIONS', 'NO_SPEAKING_TEST', 'DUPLICATE_SPEAKING', 'SPEAKING_PARTS', 'SPEAKING_SKIPPED', 'WRONG_TASK', 'WRONG_PART', 'SCOPE_SECTIONS', 'UNUSUAL_PART_LENGTH',
]);

/** Translated text for a readiness issue (the API message is the English fallback). */
export function useReadinessText(sectionTitle?: (id: string) => string | undefined) {
  const { t } = useTranslation();
  return (i: ExamReadiness['blocking'][number]) => {
    if (!KNOWN_CODES.has(i.code)) return i.message;
    const params = { ...(i.params ?? {}) } as Record<string, string | number>;
    if (typeof params.subject === 'string') params.subject = t(params.subject === 'SAT_RW' ? 'exam.kind.sat_rw' : 'exam.kind.sat_math');
    if (typeof params.skill === 'string' && params.skill in SCOPE_SKILL_KEY) params.skill = t(SCOPE_SKILL_KEY[params.skill as IeltsScopeSkill]);
    if (params.task === 'TASK_1' || params.task === 'TASK_2') params.task = t(params.task === 'TASK_1' ? 'exam.writing.task1' : 'exam.writing.task2');
    return t(`exam.readiness.${i.code}`, { section: (i.sectionId && sectionTitle?.(i.sectionId)) || '', ...params });
  };
}

/** What still blocks activation (red) and what is worth a look (amber). */
export function ExamReadinessPanel({ readiness, loading, sectionTitle }: Props) {
  const { t } = useTranslation();
  const text = useReadinessText(sectionTitle);
  if (loading || !readiness) {
    return <div className="h-16 animate-pulse rounded-xl bg-muted" aria-hidden />;
  }
  if (readiness.ready && !readiness.warnings.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-success-200 bg-success-50 px-4 py-3 text-sm font-medium text-success-800" role="status">
        <CheckCircle2 className="h-4 w-4" aria-hidden /> {t('exam.readiness.ready')}
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-xl border border-border bg-card p-4" role="status">
      <p className="text-sm font-semibold text-foreground">
        {readiness.ready ? t('exam.readiness.ready_with_notes') : t('exam.readiness.blocked', { count: readiness.blocking.length })}
      </p>
      <ul className="space-y-1.5 text-sm">
        {readiness.blocking.map((i) => (
          <li key={i.code + (i.sectionId ?? '') + i.message} className="flex gap-2 text-destructive">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {text(i)}
          </li>
        ))}
        {readiness.warnings.map((i) => (
          <li key={i.code + (i.sectionId ?? '') + i.message} className="flex gap-2 text-warning-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {text(i)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ExamReadinessPanel;
